import React, { useState } from "react";
import {
  fmtEventWhen,
  kindOf,
  eventEmoji,
  isNowEvent,
  isWholeFamilyEvent,
  eligibleAckMemberIds,
  eventCountdown,
  localizeCountdown,
} from "@/lib/events";
import { api } from "@/lib/api";
import { colorOf } from "@/lib/constants";
import { fmtTime } from "@/lib/time";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

export default function EventPill({
  event,
  members,
  viewerId,
  onClick,
  onChanged,
  tinted = false,
  // Kid-lens: when a specific member (usually an under-5 in toddler
  // mode) is provided, events they participate in get that member's
  // avatar colour as the card background; every other card collapses
  // to a single neutral tint. Lets a small child scan "which ones are
  // mine?" by colour without needing to read names.
  kidLensMember = null,
  // Young-kid mode: bump attendee avatar size so small children can
  // recognise faces on the event cards.
  bigAvatars = false,
  // Teen-plus family fade: when every human in the family is 11+,
  // mute the pastel tints so the calendar reads calmer and less
  // childish. Blends each kind.tint with ~65% paper surface so the
  // category stays identifiable by colour but in a whispered way.
  fadeTint = false,
  // Read-only: render as a non-interactive card (div, not button).
  // Used in toddler calendar-only front so the little one can see
  // events without accidentally opening the editable CalendarSheet.
  readOnly = false,
  // Compact mood-mode footprint — same content, less vertical air.
  // Mood-front shows event cards as a horizontal strip ABOVE the
  // people tiles, so any extra height pushes the actual mood grid
  // off-screen. Default false preserves the calendar/event-info
  // taller variant which is the visual hero on those screens.
  compact = false,
  // Tap on the linked-list chip — opens the lists sheet at that
  // list. Bubbles separately from the parent card click so families
  // can jump straight to the packing list without going through the
  // calendar editor first.
  onOpenLinkedList = null,
}) {
  const { lang } = useI18n();
  // Hook must come before any early-return to keep call order stable
  // across renders (react-hooks/rules-of-hooks).
  const [busy, setBusy] = useState(false);
  const kind = kindOf(event.kind);
  // Band events (school holidays, public-holiday imports) render
  // as a quiet inline-block stripe — no avatars, no countdown, no
  // ack buttons. Just enough context to know "school's out today".
  // Bands aren't tappable; opening their info page would just be
  // the title we're already showing.
  if (event.display_as === "band") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] italic text-[color:var(--ink-3)] px-2 py-0.5 rounded-full bg-[color:var(--bg-soft)]"
        data-testid={`event-pill-band-${event.id}`}
      >
        ✦ {event.title}
      </span>
    );
  }
  const isNow = isNowEvent(event);
  const wholeFamily = isWholeFamilyEvent(event, members);
  const isVirtual = event.source === "birthday";
  const needsAck = isNow && !wholeFamily && !isVirtual;
  const countdown = eventCountdown(event.starts_at, event.ends_at);

  const attendees = (event.member_ids || [])
    .map((id) => members.find((m) => m.id === id))
    .filter((m) => m && (isVirtual ? true : m.role !== "pet"));
  const arrivals = event.arrivals || {};
  const skipped = event.skipped_by || [];
  const eligibleIds = eligibleAckMemberIds(event, members);

  // Who this viewer can ack for: themselves if in attendee list.
  const ackTargets = viewerId && eligibleIds.includes(viewerId) ? [viewerId] : [];

  const myArrived = ackTargets.length > 0 && ackTargets[0] in arrivals;
  const mySkipped = ackTargets.length > 0 && skipped.includes(ackTargets[0]);

  const ack = async (action, e) => {
    e && e.stopPropagation();
    if (ackTargets.length === 0) return;
    setBusy(true);
    try {
      const updated = await api.ackEvent(event.id, {
        member_id: ackTargets[0],
        action,
      });
      onChanged && onChanged(updated);
    } catch {
      toast.error("Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  const multipleAttendees = eligibleIds.length > 1;
  const arrivedLabel = multipleAttendees ? "We’re there" : "I’m there";

  // Kid-lens resolver: only active in toddler mode. Participated
  // events get the kid's avatar colour; everything else drops to a
  // flat quiet surface so the kid's cards pop hard.
  const kidLensBg = (() => {
    if (!kidLensMember) return null;
    const participates =
      (event.member_ids || []).includes(kidLensMember.id) ||
      wholeFamily ||
      (isVirtual && attendees[0]?.id === kidLensMember.id);
    if (participates) return colorOf(kidLensMember.color).hex;
    return "var(--bg-soft)";
  })();

  // Short date variant for the pill footer — drops the weekday when
  // a countdown chip is showing next to it (the chip already carries
  // that "in N days" context, so "Tue, May 19 · IN 20 DAYS" was both
  // duplicated and truncating to "TUE, MA..." on narrow 2-col grids).
  // Keeps the full label for Today/Tomorrow (no chip shown anyway).
  const fmtShortWhen = (iso, endIso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const isDa = lang === "da";
    const dateLocale = isDa ? "da-DK" : "en-US";
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow =
      d.getFullYear() === tomorrow.getFullYear() &&
      d.getMonth() === tomorrow.getMonth() &&
      d.getDate() === tomorrow.getDate();
    const dateLabel = sameDay
      ? (isDa ? "I dag" : "Today")
      : isTomorrow
      ? (isDa ? "I morgen" : "Tomorrow")
      : d.toLocaleDateString(dateLocale, { month: "short", day: "numeric" });
    const isAllDay = d.getHours() === 0 && d.getMinutes() === 0;
    if (isAllDay) return dateLabel;
    const timeStr = fmtTime(d);
    // Multi-day range
    if (endIso) {
      const e = new Date(endIso);
      if (!Number.isNaN(e.getTime())) {
        const startDay = `${d.getMonth()}-${d.getDate()}`;
        const endDay = `${e.getMonth()}-${e.getDate()}`;
        if (startDay !== endDay) {
          const endLabel = e.toLocaleDateString(dateLocale, {
            month: "short",
            day: "numeric",
          });
          return `${dateLabel} → ${endLabel}`;
        }
      }
    }
    return isDa ? `${dateLabel} kl. ${timeStr}` : `${dateLabel} · ${timeStr}`;
  };
  const hasCountdownChip = !!countdown && !isNow && countdown !== "today";
  const whenLabelForPill = hasCountdownChip
    ? fmtShortWhen(event.starts_at, event.ends_at)
    : fmtEventWhen(event.starts_at, event.ends_at, lang);

  // Teen-plus fade: blend the kind tint with ~50% paper via color-mix
  // so the pastel whispers instead of shouts, but keeps enough chroma
  // that each kind stays visually distinct against the cream page
  // background (the older 60% paper mix made warm yellows look
  // sickly and washed-out on teen-mode dashboards). CSS color-mix is
  // supported in every evergreen browser since 2023.
  const fadedTint = `color-mix(in srgb, var(--bg) 50%, ${kind.tint})`;
  const resolvedBg = kidLensBg
    ? kidLensBg
    : tinted
      ? fadeTint
        ? fadedTint
        : kind.tint
      : undefined;

  // Birthday variant: mirrors the regular pill shape (emoji-left,
  // attendee-avatar-right) so a vertical list of events scans with
  // one consistent rhythm. Pink kind tint + subtle confetti
  // decorations signal "this is a birthday" without breaking the
  // card-layout grammar.
  if (isVirtual && event.kind === "birthday") {
    const birthdayPerson = attendees[0];
    const whenLabel = whenLabelForPill;
    const dayOnly = whenLabel.split(" · ")[0];

    const birthdayBg = resolvedBg
      ? resolvedBg
      : fadeTint
        ? fadedTint
        : kind.tint;

    const Root = readOnly ? "div" : "button";
    return (
      <Root
        onClick={readOnly ? undefined : onClick}
        className={`ff-card ${readOnly ? "" : "ff-card-hover"} text-left ${compact ? "py-2.5 px-3 gap-1" : "p-3 gap-1.5"} rounded-[10px] flex-1 min-w-[180px] max-w-[280px] flex flex-col relative overflow-hidden`}
        data-testid={`event-pill-${event.id}`}
        style={{ background: birthdayBg, ...(readOnly ? { cursor: "default" } : {}) }}
        aria-disabled={readOnly || undefined}
      >
        {/* Confetti decorations — float in the corners on the
            tint layer so the content grid above stays quiet and
            scannable. Pointer-events disabled so they never block
            the tap. */}
        <span className="pointer-events-none absolute top-1 right-2 text-sm opacity-70 rotate-12" aria-hidden="true">🎊</span>
        <span className="pointer-events-none absolute bottom-1 left-2 text-sm opacity-60 -rotate-12" aria-hidden="true">🎈</span>

        <div className="flex items-start gap-2 relative z-[1]">
          <span
            className="text-2xl leading-none flex-shrink-0"
            aria-hidden="true"
            data-testid={`event-pill-birthday-emoji-${event.id}`}
          >
            🎉
          </span>
          <div className="font-display text-base leading-snug truncate tracking-tight flex-1">
            {event.title}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 leading-tight relative z-[1]">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="label-tiny text-[color:var(--ink-2)] truncate">{dayOnly}</span>
            {countdown && countdown !== "today" && (
              <span
                className="label-tiny text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--bg)]/70 text-[color:var(--ink-3)] whitespace-nowrap flex-shrink-0"
                data-testid={`event-countdown-${event.id}`}
              >
                {localizeCountdown(countdown, lang)}
              </span>
            )}
          </div>
          {/* Single attendee avatar on the right — same position as
              regular cards' attendee stack. Birthday events always
              have exactly one attendee (the person whose birthday
              it is), so no +N overflow. */}
          {birthdayPerson && (
            <span
              className="w-6 h-6 rounded-full bg-[color:var(--bg)] border border-[color:var(--surface)] flex items-center justify-center text-[11px] overflow-hidden flex-shrink-0"
              title={birthdayPerson.name}
              data-testid={`birthday-avatar-${birthdayPerson.id}`}
            >
              {birthdayPerson.avatar_image ? (
                <img
                  src={birthdayPerson.avatar_image}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                birthdayPerson.avatar_emoji || "🙂"
              )}
            </span>
          )}
        </div>
      </Root>
    );
  }

  const Root = readOnly ? "div" : "button";
  return (
    <Root
      onClick={readOnly ? undefined : onClick}
      className={`ff-card ${readOnly ? "" : "ff-card-hover"} text-left ${compact ? "py-2 px-2.5 gap-0.5" : "p-3 gap-1.5"} rounded-[10px] flex-1 min-w-[180px] max-w-[280px] flex flex-col relative ${
        isNow ? "ring-1 ring-[color:var(--ink)]/30" : ""
      }`}
      data-testid={`event-pill-${event.id}`}
      style={{
        ...(resolvedBg ? { background: resolvedBg } : {}),
        ...(readOnly ? { cursor: "default" } : {}),
      }}
      aria-disabled={readOnly || undefined}
    >
      <div className="flex items-start gap-2">
        <span className={`${compact ? "text-xl" : "text-2xl"} leading-none flex-shrink-0`} aria-hidden="true">
          {eventEmoji(event)}
        </span>
        <div className={`font-display ${compact ? "text-sm" : "text-base"} leading-snug truncate tracking-tight flex-1 text-[color:var(--accent)]`}>
          {event.title}
          {event.source === "ics" && (
            <span
              className="ml-1.5 align-middle inline-block rounded-full bg-[color:var(--bg-soft)] px-1.5 py-0.5 label-tiny text-[color:var(--ink-3)]"
              title="Imported from a connected calendar — open to assign attendees"
              data-testid={`event-source-ics-${event.id}`}
            >
              from Google
            </span>
          )}
        </div>
        {isNow && (
          <span
            className="flex items-center gap-1 flex-shrink-0"
            data-testid={`event-now-${event.id}`}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[color:var(--ink)] opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[color:var(--ink)]" />
            </span>
            <span className="label-tiny">Now</span>
          </span>
        )}
      </div>
      {/* Date line on the left, countdown chip on the right. Pulling
          the countdown out of the inline string stops it wrapping to
          its own line on narrow phones (the "IN 18 / DAYS" issue). */}
      <div className="flex items-center justify-between gap-2 leading-tight">
        <span className="label-tiny text-[color:var(--ink)] truncate">
          {isNow
            ? compact
              ? whenLabelForPill /* compact mode: NOW pill already says it */
              : (lang === "da" ? "Sker nu" : "Happening now")
            : whenLabelForPill}
        </span>
        {countdown && !isNow && countdown !== "today" && (
          <span
            className="label-tiny text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--bg)]/70 text-[color:var(--ink)] whitespace-nowrap flex-shrink-0"
            data-testid={`event-countdown-${event.id}`}
          >
            {localizeCountdown(countdown, lang)}
          </span>
        )}
      </div>
      {attendees.length > 0 && !needsAck && (
        <div className="flex -space-x-1">
          {attendees.slice(0, 4).map((m) =>
            m.avatar_image ? (
              <img
                key={m.id}
                src={m.avatar_image}
                alt={m.name}
                title={m.name}
                className={`${
                  bigAvatars ? "w-8 h-8" : compact ? "w-5 h-3.5" : "w-5 h-5"
                } rounded-full object-cover border border-[color:var(--surface)]`}
              />
            ) : (
              <span
                key={m.id}
                className={`${
                  bigAvatars
                    ? "w-8 h-8 text-base"
                    : compact
                      ? "w-5 h-3.5 text-[10px]"
                      : "w-5 h-5 text-[11px]"
                } rounded-full bg-[color:var(--bg)] border border-[color:var(--surface)] flex items-center justify-center`}
                title={m.name}
              >
                {m.avatar_emoji}
              </span>
            )
          )}
          {attendees.length > 4 && (
            <span className="label-tiny ml-2 self-center">
              +{attendees.length - 4}
            </span>
          )}
        </div>
      )}
      {/* Linked-list pin — quietly tells the family there's a list
          attached to this event. Clickable chip jumps straight to the
          list sheet (skipping the calendar editor). We use a span with
          role="button" because EventPill itself is rendered as a
          <button>, and nesting buttons is invalid HTML. */}
      {event.linked_list_id && (
        <span
          role={onOpenLinkedList ? "button" : undefined}
          tabIndex={onOpenLinkedList ? 0 : undefined}
          onClick={
            onOpenLinkedList
              ? (e) => {
                  e.stopPropagation();
                  onOpenLinkedList(event.linked_list_id);
                }
              : undefined
          }
          onKeyDown={
            onOpenLinkedList
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenLinkedList(event.linked_list_id);
                  }
                }
              : undefined
          }
          className={`label-tiny inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[color:var(--bg)]/70 text-[color:var(--ink-3)] self-start ${
            onOpenLinkedList ? "cursor-pointer hover:bg-[color:var(--bg)]" : ""
          }`}
          data-testid={`event-linked-list-${event.id}`}
          title="Open the linked list"
        >
          📝 List
        </span>
      )}
      {/* Photo-attachment indicator — quietly tells the family there's
          a ticket / flyer / receipt pinned to this event. Tapping the
          card itself opens EventInfoPage where the photo lives, so
          this chip is read-only. */}
      {event.attachment_image && (
        <span
          className="label-tiny inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[color:var(--bg)]/70 text-[color:var(--ink-3)] self-start"
          data-testid={`event-attachment-indicator-${event.id}`}
          title="A photo is attached — tap the event to view"
        >
          📎 Photo
        </span>
      )}
      {/* Silent-merge footnote — when two parents independently logged
          the same event, the backend folds them into one row and lists
          the others here. Quiet so it doesn't crowd the card. */}
      {(() => {
        const otherIds = event.also_added_by_member_ids || [];
        if (!otherIds.length) return null;
        const names = otherIds
          .map((mid) => members.find((m) => m.id === mid)?.name)
          .filter(Boolean);
        if (!names.length) return null;
        const phrase =
          names.length === 1
            ? names[0]
            : names.length === 2
              ? `${names[0]} & ${names[1]}`
              : `${names.slice(0, -1).join(", ")} & ${names.slice(-1)[0]}`;
        return (
          <span
            className="label-tiny text-[10px] text-[color:var(--ink-3)] italic"
            data-testid={`event-also-added-${event.id}`}
            title={`${phrase} also added this`}
          >
            also added by {phrase}
          </span>
        );
      })()}
      {(() => {
        if (!needsAck || readOnly) return null;
        const hasButtons = ackTargets.length > 0;
        const hasRollup = Object.keys(arrivals).length > 0 || skipped.length > 0;
        // "Not for you" was removed app-wide — attendee avatars
        // already carry that message (no avatar = not for you).
        if (!hasButtons && !hasRollup) return null;
        return (
          <div className="pt-1 flex flex-wrap items-center gap-1.5">
            {hasButtons && (
              <>
                <span
                  onClick={(e) => ack("arrived", e)}
                  className={`ff-chip cursor-pointer ${myArrived ? "is-active" : ""} ${busy ? "opacity-60" : ""}`}
                  data-testid={`event-arrived-${event.id}`}
                >
                  {myArrived ? "✓ " : ""}{arrivedLabel}
                </span>
                <span
                  onClick={(e) => ack("skipped", e)}
                  className={`ff-chip cursor-pointer ${mySkipped ? "is-active" : ""} ${busy ? "opacity-60" : ""}`}
                  data-testid={`event-skipped-${event.id}`}
                >
                  {mySkipped ? "✓ " : ""}Skipped
                </span>
              </>
            )}
            {hasRollup && (
              <span className="label-tiny text-[color:var(--ink-2)] ml-auto">
                {Object.keys(arrivals).length > 0 &&
                  `${Object.keys(arrivals).length} there`}
                {Object.keys(arrivals).length > 0 && skipped.length > 0 && " · "}
                {skipped.length > 0 && `${skipped.length} skipped`}
              </span>
            )}
          </div>
        );
      })()}
    </Root>
  );
}
